import { RefObject } from 'react'

import {
  EmptyState,
  Pill,
  TokenBalanceView,
  TokenDisplayView,
} from '~/components/component-library'
import { SupportedVmTypes } from '~/constants/chains'
import { useActiveOrgSmartAccountsChainIds } from '~/hooks/org'
import { useRelayNetworks } from '~/hooks/useRelayNetworks'
import { useMultipleTokensMetadata } from '~/hooks/useTokenMetadata'
import { getChainLabel } from '~/utils/chainLabels'
import { cn } from '~/utils/tailwind-utils'
import { getVaultAssetSymbol, isLegacyVault, isVaultToken } from '~/utils/vault'

export type TokenListItem = {
  address: string
  chainId: number
  balance?: { address: string; chainId: number; amount: bigint }
  isRelayNetwork?: boolean
}

type TokenSelectorTokenListProps = {
  items: TokenListItem[]
  highlightedIndex: number
  highlightedRef: RefObject<HTMLDivElement | null>
  highlightText?: string
  onSelect: (token: { address: string; chainId: number }) => void
  onHighlight: (index: number) => void
}

export function TokenSelectorTokenList({
  items,
  highlightedIndex,
  highlightedRef,
  highlightText,
  onSelect,
  onHighlight,
}: TokenSelectorTokenListProps) {
  const metadataItems = useMultipleTokensMetadata(
    items.map((item) => ({
      address: item.address as `0x${string}`,
      chainId: item.chainId,
    })),
    { includePrice: true },
  )
  const activeChainIds = useActiveOrgSmartAccountsChainIds()
  const { data: relayNetworks } = useRelayNetworks({
    enabled: true,
    vmTypes: SupportedVmTypes,
  })

  if (items.length === 0) {
    return (
      <div className="flex h-20 w-full items-center p-1">
        <EmptyState size="compact">No tokens found</EmptyState>
      </div>
    )
  }

  return (
    <div role="listbox" aria-label="Token list">
      {items.map((item, index) => {
        const isHighlighted = highlightedIndex === index
        const metadata = metadataItems[index]
        const address = item.address as `0x${string}`
        const isPosition = isVaultToken(item.chainId, address)
        const vaultAssetSymbol = isPosition
          ? getVaultAssetSymbol(item.chainId, address)
          : undefined
        const symbol = vaultAssetSymbol || metadata?.symbol || 'UNKNOWN'
        const chainLabel = getChainLabel(item.chainId, {
          relayNetwork: relayNetworks?.find(
            (network) => network.id === item.chainId,
          ),
        })
        return (
          <div
            key={`${item.chainId}-${item.address}`}
            ref={isHighlighted ? highlightedRef : undefined}
            data-testid="token-selector-option"
            role="option"
            aria-selected={isHighlighted}
            className={cn(
              'flex w-full cursor-pointer items-center justify-between rounded p-2 py-1.5',
              isHighlighted && 'bg-surface-muted',
            )}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() =>
              onSelect({
                address: item.address,
                chainId: item.chainId,
              })
            }
            onMouseEnter={() => onHighlight(index)}
          >
            <TokenDisplayView
              address={address}
              chainId={item.chainId}
              layout="two-line"
              size="small"
              hideInactiveChainIndicator={item.isRelayNetwork}
              symbol={symbol}
              logoUri={metadata?.logoUri}
              isPosition={isPosition}
              isInactiveChain={!activeChainIds.includes(item.chainId)}
              chainLabel={chainLabel}
              highlightText={highlightText}
              suffix={
                isLegacyVault(address) ? (
                  <Pill label="Legacy" size="small" />
                ) : undefined
              }
            />
            {item.balance && (
              <TokenBalanceView
                address={item.balance.address as `0x${string}`}
                chainId={item.balance.chainId}
                tokenAmount={item.balance.amount}
                layout="standard"
                size="small"
                direction="neutral"
                tokenMetadata={metadata}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
